import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import Label from 'src/components/label';
import { UserType } from 'src/type/user';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';

type Props = {
  user: UserType;
  open: boolean;
  onClose: VoidFunction;
  handleReload: VoidFunction;
  onUserUpdate?: (updatedUser: UserType) => void;
};

export default function EditEnableDialog({
  user,
  open,
  onClose,
  handleReload,
  onUserUpdate,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      const newStatus = !user.isEnable;

      await axios.patch(endpoints.users.update(user.uid), {
        isEnable: newStatus,
      });

      // Update the user object locally
      const updatedUser = { ...user, isEnable: newStatus };
      onUserUpdate?.(updatedUser);

      handleReload();
      onClose();
      enqueueSnackbar(`อัพเดทสถานะบัญชีสำเร็จ - ${newStatus ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`, {
        variant: 'success',
      });
    } catch (error) {
      toastError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionText = () => (user.isEnable ? 'ปิดใช้งานบัญชี' : 'เปิดใช้งานบัญชี');

  const getAlertMessage = () =>
    user.isEnable
      ? 'การปิดใช้งานบัญชีจะทำให้ผู้ใช้ไม่สามารถเข้าสู่ระบบได้'
      : 'การเปิดใช้งานบัญชีจะทำให้ผู้ใช้สามารถเข้าสู่ระบบได้อีกครั้ง';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getActionText()}</DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Alert severity={user.isEnable ? 'warning' : 'info'}>{getAlertMessage()}</Alert>

          <Stack spacing={1}>
            <Typography variant="subtitle2">ข้อมูลผู้ใช้</Typography>
            <Typography variant="body2" color="text.secondary">
              ชื่อ: {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              อีเมล: {user.email}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              สถานะปัจจุบัน:
              <Label
                variant="soft"
                color={(user.isEnable === true && 'success') || 'error'}
                sx={{ width: 'fit-content', px: 1 }}
              >
                {user.isEnable === true ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
              </Label>
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          ยกเลิก
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={isSubmitting}
          color={user.isEnable ? 'error' : 'success'}
        >
          {getActionText()}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
